import { KycIntegrationMethodStatus } from '@nemuru-eng/js-api-client';

import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import {
  KycIntegrationMethodOwner,
  OwnedKycIntegrationMethod,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { usePerformManualVerificationMutation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/CompleteKYC/hooks/usePerformManualVerificationMutation';
import { useSendVerificationEmailMutation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/CompleteKYC/hooks/useSendVerificationEmailMutation';
import { KYCAction } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/CompleteKYC/KycAction';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

export const KYCActionWrapper = ({
  kycIntegrationMethod,
  paymentMethodDetailEntity,
}: {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  kycIntegrationMethod: OwnedKycIntegrationMethod;
}) => {
  const { getTextIdTranslation } = useTranslation();
  const sendVerificationEmailMutation = useSendVerificationEmailMutation(
    paymentMethodDetailEntity,
    kycIntegrationMethod.owner,
  );
  const performManualVerificationMutation = usePerformManualVerificationMutation(
    paymentMethodDetailEntity,
    kycIntegrationMethod.owner,
  );

  const isCompleted =
    kycIntegrationMethod.status === KycIntegrationMethodStatus.COMPLETED ||
    kycIntegrationMethod.status === KycIntegrationMethodStatus.ACCEPTED;
  const isPending = kycIntegrationMethod.status === KycIntegrationMethodStatus.PENDING;
  const hasFailedAllAttempts =
    kycIntegrationMethod.status === KycIntegrationMethodStatus.FAILED && !kycIntegrationMethod.allowedResend;
  const hasFailedSomeAttempts =
    kycIntegrationMethod.status === KycIntegrationMethodStatus.FAILED && kycIntegrationMethod.allowedResend;

  const emailVerification = {
    onSubmit: sendVerificationEmailMutation.mutateAsync,
    isLoading: sendVerificationEmailMutation.isLoading,
    isError: sendVerificationEmailMutation.isError,
  };

  const manualVerification = {
    allowed: kycIntegrationMethod.allowedSetManual,
    onSubmit: performManualVerificationMutation.mutateAsync,
    onCancel: performManualVerificationMutation.reset,
    isLoading: performManualVerificationMutation.isLoading,
    isError: performManualVerificationMutation.isError,
  };

  if (isCompleted) {
    return null;
  }

  const ownerLabel = getTextIdTranslation(`KYC_OWNER_LABEL_${kycIntegrationMethod.owner}`);

  if (hasFailedAllAttempts) {
    return (
      <KYCAction
        title="KYC_STEP_FAILED_TITLE"
        description={['KYC_STEP_HARD_FAILED_DESCRIPTION_P1', 'KYC_STEP_HARD_FAILED_DESCRIPTION_P2']}
        owner={kycIntegrationMethod.owner}
        manualVerification={manualVerification}
      />
    );
  }

  if (hasFailedSomeAttempts) {
    return (
      <KYCAction
        title="KYC_STEP_FAILED_TITLE"
        description={[
          'KYC_STEP_SOFT_FAILED_DESCRIPTION_P1',
          'KYC_STEP_SOFT_FAILED_DESCRIPTION_P2',
          'KYC_STEP_SOFT_FAILED_DESCRIPTION_P3',
          'KYC_STEP_SOFT_FAILED_DESCRIPTION_P4',
        ]}
        owner={kycIntegrationMethod.owner}
        emailVerification={{
          ...emailVerification,
          buttonText: 'KYC_STEP_SOFT_FAILED_BUTTON_TEXT',
        }}
        manualVerification={manualVerification}
      />
    );
  }

  if (isPending) {
    const onboardingDetails =
      kycIntegrationMethod.owner === KycIntegrationMethodOwner.BORROWER
        ? paymentMethodDetailEntity.borrowerOnboardingDetails()
        : paymentMethodDetailEntity.coBorrowerOnboardingDetails();

    const email = onboardingDetails?.invitation.email;

    return (
      <KYCAction
        title="KYC_STEP_PENDING_TITLE"
        description={[
          {
            text: email ? 'KYC_STEP_PENDING_DESCRIPTION_P1' : 'KYC_STEP_PENDING_DESCRIPTION_P1_NO_EMAIL',
            variables: email ? [email, ownerLabel] : [ownerLabel],
          },
          'KYC_STEP_PENDING_DESCRIPTION_P2',
        ]}
        owner={kycIntegrationMethod.owner}
        emailVerification={{
          ...emailVerification,
          buttonText: 'KYC_STEP_PENDING_BUTTON_TEXT',
        }}
        manualVerification={manualVerification}
      />
    );
  }

  return (
    <KYCAction
      title="KYC_STEP_INITIAL_TITLE"
      owner={kycIntegrationMethod.owner}
      description={[
        {
          text: 'KYC_STEP_INITIAL_DESCRIPTION_P1',
          variables: [ownerLabel],
        },
      ]}
      emailVerification={{
        ...emailVerification,
        buttonText: 'KYC_STEP_INITIAL_BUTTON_TEXT',
      }}
    />
  );
};
